import React, { createContext, useContext, useState, useEffect } from 'react';

interface Settings {
  companyName: string;
  email: string;
  phone: string;
  address: string;
  businessHours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  socialMedia: {
    facebook: string;
    instagram: string;
    youtube: string;
    tiktok: string;
  };
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Settings) => void;
}

const defaultSettings: Settings = {
  companyName: 'Travis Tech LLC',
  email: 'support@travistechrepair.com',
  phone: '850-273-8264',
  address: '18117 Biscayne Blvd Suite #4281, Miami, FL 33160',
  businessHours: {
    monday: '9:00 AM - 8:00 PM',
    tuesday: '9:00 AM - 8:00 PM',
    wednesday: '9:00 AM - 8:00 PM',
    thursday: '9:00 AM - 8:00 PM',
    friday: '9:00 AM - 8:00 PM',
    saturday: '10:00 AM - 2:00 PM',
    sunday: 'Closed'
  },
  socialMedia: {
    facebook: 'https://www.facebook.com/TheRealTravisTech?mibextid=LQQJ4d',
    instagram: 'https://www.instagram.com/_travistech?igsh=MWZoZW5ldTV2c2RoeA%3D%3D&utm_source=qr',
    youtube: 'https://youtube.com/@travis_tech?si=w_l5UyvQ57BTU4E8',
    tiktok: 'https://www.tiktok.com/@travistechrepairs?_t=8r9251nDGjz&_r=1'
  }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Try to load settings from localStorage
    try {
      const savedSettings = localStorage.getItem('siteSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Error loading settings from localStorage:', error);
      // If there's an error, use default settings
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Only save to localStorage after initial load
    if (isLoaded) {
      try {
        localStorage.setItem('siteSettings', JSON.stringify(settings));
      } catch (error) {
        console.error('Error saving settings to localStorage:', error);
      }
    }
  }, [settings, isLoaded]);

  const updateSettings = (newSettings: Settings) => {
    setSettings(newSettings);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}